// ============================================
// PAGE - Reportes
// ============================================

import { useState, useEffect, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { goToPremium } from '@/lib/premium';
import {
  useRecaudacionChart,
  useTopProductos,
  useUtilidadCosto,
  useReporteDiario,
  useAlertasInventario,
  useTotalesGenerales,
  useDetalleVentasProducto,
  useDetalleVentasCategoriaAlmacen,
  useIniciarBusquedaReportes,
  useSession,
  useUIStore,
  getFechaHoraChile,
  getFechaHoyChile,
  formatFechaLocal,
  useProductoVendibleById,
  reportesRepository,
  type ApiError,
} from '@almacenes/core';
import { AddStockModal } from '@/components/AddStockModal';
import { capitalize } from '@almacenes/ui';
import { MdSearch, MdDownload } from 'react-icons/md';
import { FaCrown } from 'react-icons/fa';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
  LabelList,
} from 'recharts';

const COLORS = ['#43BFFF', '#FFB84D', '#22C55E', '#EF4444', '#8B5CF6', '#EC4899', '#F59E0B', '#10B981', '#6366F1', '#14B8A6'];

// Antigüedad máxima del plan gratuito, en días. Espeja reportes.freemium.dias-max-antiguedad del
// backend (que es el que realmente lo hace cumplir): si negocio cambia el valor, cambiar ambos.
const DIAS_MAX_FREE = 30;

// Fecha YYYY-MM-DD de hace `dias` días, partiendo del día actual en zona Chile. Es el patrón de
// MIGRACION_FECHAS.md ("Filtros de Reportes"): getFechaHoraChile() + setDate + formatFechaLocal.
const haceDiasChile = (dias: number): string => {
  const fecha = getFechaHoraChile();
  fecha.setDate(fecha.getDate() - dias);
  return formatFechaLocal(fecha);
};

// Rango por defecto al abrir la pantalla: 30 días (calza con el default del backend,
// hasta.minusDays(30), y con el máximo del plan gratuito). Para premium el máximo consultable
// sigue siendo 1 año (getFechaLimite, dentro del componente) -- este default no lo reduce,
// solo prellena los inputs.
// Es una función de módulo (no un helper dentro del componente) para poder usarla
// en el inicializador de useState sin problemas de orden de declaración.
const getRangoPorDefecto = () => ({ desde: haceDiasChile(30), hasta: getFechaHoyChile() });

// El backend responde los errores de validación con fieldErrors como lista de {field, message}
// (el tipo ApiError lo declara como Record, pero no es lo que llega), y el message genérico
// "Hay campos inválidos" no sirve al usuario: se prefiere el detalle del primer campo.
const mensajeDeError = (error: unknown): string => {
  const apiError = error as Partial<ApiError> | undefined;
  const fieldErrors = apiError?.fieldErrors as unknown;
  if (Array.isArray(fieldErrors) && fieldErrors.length > 0 && typeof fieldErrors[0]?.message === 'string') {
    return fieldErrors[0].message;
  }
  return apiError?.message || 'No se pudo realizar la búsqueda. Intenta nuevamente.';
};

// Wrapper que carga el ProductoVendible y abre AddStockModal
const AlertaStockModal = ({
  productoAlmacenId,
  onClose,
}: {
  productoAlmacenId: number;
  onClose: () => void;
}) => {
  const { data: producto, isLoading, error } = useProductoVendibleById(productoAlmacenId);

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-white border-t-transparent" />
      </div>
    );
  }

  if (error || !producto) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={onClose}>
        <div className="bg-white rounded-xl p-6 max-w-sm mx-4 shadow-xl" onClick={(e) => e.stopPropagation()}>
          <p className="text-red-600 font-semibold mb-4">No se pudo cargar el producto. Intenta nuevamente.</p>
          <button
            onClick={onClose}
            className="w-full py-2 rounded-lg text-white font-semibold"
            style={{ backgroundColor: '#11194F' }}
          >
            Cerrar
          </button>
        </div>
      </div>
    );
  }

  return <AddStockModal isOpen onClose={onClose} producto={producto} />;
};

export const ReportesPage = () => {
  const addToast = useUIStore((state) => state.addToast);
  const { data: session } = useSession();
  const hasPremiumAccess = session?.hasPremiumAccess || false;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { mutateAsync: iniciarBusqueda, isPending: iniciandoBusqueda } = useIniciarBusquedaReportes();

  // Si viene con ?tab=alertas desde el dashboard, activar esa pestaña directamente
  const tabFromUrl = searchParams.get('tab');
  const vieneDeNavegacion = tabFromUrl === 'alertas';

  // Rango en edición: lo que el usuario ve/teclea en los inputs de fecha.
  const [fechaDesdeInput, setFechaDesdeInput] = useState(() => getRangoPorDefecto().desde);
  const [fechaHastaInput, setFechaHastaInput] = useState(() => getRangoPorDefecto().hasta);
  // Rango APLICADO: el que alimenta las consultas y la exportación a Excel. Solo lo
  // escribe handleBuscar, al presionar "Buscar" -- por eso arranca vacío (sin
  // búsqueda automática al entrar, aunque los inputs ya muestren el rango por defecto).
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');
  const [busquedaRealizada, setBusquedaRealizada] = useState(false);
  // Comprobante de la búsqueda aplicada (lo entrega POST /reportes/busquedas al presionar Buscar):
  // se envía en las consultas siguientes y en la exportación. Solo ese POST descuenta cupo.
  const [busquedaId, setBusquedaId] = useState<number | undefined>(undefined);
  // Cupo diario del plan gratuito tras la última búsqueda; null en premium o antes de buscar.
  const [cupo, setCupo] = useState<{ limiteDiario: number | null; restantes: number | null } | null>(null);
  const [mostrarPlanes, setMostrarPlanes] = useState(false);
  const [paginaActual, setPaginaActual] = useState(0);
  const [paginaDetalleProducto, setPaginaDetalleProducto] = useState(0);
  const [tabActiva, setTabActiva] = useState<'ventas' | 'alertas' | 'detalle-producto' | 'por-categoria'>(
    vieneDeNavegacion ? 'alertas' : 'ventas'
  );

  // Sincronizar tab activo si el query param cambia después del montaje
  useEffect(() => {
    if (vieneDeNavegacion) setTabActiva('alertas');
  }, [vieneDeNavegacion]);

  // Al salir de Reportes, cancela cualquier consulta en vuelo: sin esto, las
  // peticiones seguirían corriendo en el servidor aunque nadie vaya a leer el resultado.
  useEffect(() => {
    return () => {
      queryClient.cancelQueries({ queryKey: ['reportes'] });
    };
  }, [queryClient]);

  const [exportando, setExportando] = useState(false);
  const [alertaStockProductoId, setAlertaStockProductoId] = useState<number | null>(null);
  const [alertasSort, setAlertasSort] = useState<{ campo: 'codigo' | 'nombreProducto' | 'fechaVencimiento' | 'stock' | 'estado'; dir: 'asc' | 'desc' } | null>(null);

  // Fecha límite: 1 año atrás en premium, DIAS_MAX_FREE días en el plan gratuito
  const getFechaLimite = () => {
    if (!hasPremiumAccess) return haceDiasChile(DIAS_MAX_FREE);
    const fecha = getFechaHoraChile();
    fecha.setFullYear(fecha.getFullYear() - 1);
    return formatFechaLocal(fecha);
  };

  // Fecha máxima (hoy)
  const getFechaMaxima = () => {
    return getFechaHoyChile();
  };

  // Convertir formato yyyy-mm-dd a dd-mm-yyyy
  const formatearFechaEspanol = (fechaISO: string): string => {
    if (!fechaISO) return '';
    const [year, month, day] = fechaISO.split('-');
    return `${day}-${month}-${year}`;
  };

  // Truncar nombres de productos para el gráfico
  const truncarNombre = (nombre: string, maxLength: number = 20): string => {
    if (nombre.length <= maxLength) return nombre;
    return nombre.substring(0, maxLength) + '...';
  };

  // Formatear nombre de producto
  const formatearNombreProducto = (nombre: string): string => {
    // Primero reemplazar (Sin descripción) por Calculadora
    const nombreProcesado = nombre === '(Sin descripción)' ? '(Calculadora)' : nombre;
    
    // Aplicar capitalize a cada palabra
    return capitalize(nombreProcesado);
  };

  // Formatear cantidad con coma decimal y máximo 2 decimales (solo si tiene decimales significativos)
  const formatearCantidad = (cantidad: number): string => {
    if (cantidad % 1 === 0) {
      // Es un número entero, mostrar sin decimales
      return cantidad.toString();
    } else {
      // Tiene decimales, formatear con coma y 2 decimales
      return cantidad.toFixed(2).replace('.', ',');
    }
  };

  const exportarExcel = async () => {
    const { Workbook } = await import('exceljs');
    const wb = new Workbook();
    wb.creator = 'Almacenes Digitales';
    wb.created = new Date();

    // Colores de marca
    const C = {
      dark:        'FF11194F', // azul oscuro (texto primario)
      brand:       'FF43BFFF', // azul primario
      bgLight:     'FFF3F4F9', // fondo general
      white:       'FFFFFFFF',
      green:       'FF22C55E',
      greenLight:  'FFD1FAE5',
      red:         'FFEF4444',
      redLight:    'FFFEE2E2',
      orange:      'FFFFB84D',
      orangeLight: 'FFFFF7ED',
      border:      'FFE5E7EB',
    };

    const CLP = '$#,##0';

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const styleTitle = (row: any, numCols: number, bgColor = C.brand) => {
      row.height = 34;
      row.font = { name: 'Calibri', bold: true, color: { argb: C.white }, size: 13 };
      row.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: bgColor } };
      row.alignment = { horizontal: 'left', vertical: 'middle', indent: 1 };
      for (let i = 1; i <= numCols; i++) row.getCell(i).border = {};
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const styleHeader = (row: any, numCols: number) => {
      row.height = 26;
      row.font = { name: 'Calibri', bold: true, color: { argb: C.white }, size: 10 };
      row.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: C.dark } };
      row.alignment = { horizontal: 'center', vertical: 'middle' };
      for (let i = 1; i <= numCols; i++) {
        row.getCell(i).border = {
          top:    { style: 'medium', color: { argb: C.brand } },
          bottom: { style: 'medium', color: { argb: C.brand } },
          left:   { style: 'thin',   color: { argb: C.dark  } },
          right:  { style: 'thin',   color: { argb: C.dark  } },
        };
      }
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const styleData = (row: any, numCols: number, isAlt: boolean) => {
      row.height = 20;
      if (isAlt) row.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: C.bgLight } };
      for (let i = 1; i <= numCols; i++) {
        row.getCell(i).border = {
          bottom: { style: 'thin', color: { argb: C.border } },
          left:   { style: 'thin', color: { argb: C.border } },
          right:  { style: 'thin', color: { argb: C.border } },
        };
      }
    };

    // ===== Hoja 1: Resumen General =====
    if (totalesGeneralesData) {
      const ws = wb.addWorksheet('Resumen');
      ws.columns = [
        { key: 'a', width: 26 },
        { key: 'b', width: 22 },
      ];

      const tRow = ws.addRow(['Resumen General', '']);
      ws.mergeCells('A1:B1');
      styleTitle(tRow, 2);

      const hRow = ws.addRow(['Concepto', 'Monto ($)']);
      styleHeader(hRow, 2);

      const items: [string, number][] = [
        ['Efectivo',      Math.round(totalesGeneralesData.totalEfectivo)],
        ['Débito',        Math.round(totalesGeneralesData.totalDebito)],
        ['Crédito',       Math.round(totalesGeneralesData.totalCredito)],
        ['Fiado',         Math.round(totalesGeneralesData.totalFiado)],
        ['Transferencia', Math.round(totalesGeneralesData.totalTransferencia)],
      ];
      items.forEach((item, idx) => {
        const row = ws.addRow(item);
        styleData(row, 2, idx % 2 !== 0);
        row.getCell(2).numFmt = CLP;
        row.getCell(2).alignment = { horizontal: 'right' };
      });

      const totalRow = ws.addRow(['Total Venta', Math.floor(totalesGeneralesData.totalVenta)]);
      totalRow.height = 24;
      totalRow.font = { name: 'Calibri', bold: true, color: { argb: C.dark }, size: 11 };
      totalRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE0F2FE' } };
      for (let i = 1; i <= 2; i++) {
        totalRow.getCell(i).border = {
          top:    { style: 'medium', color: { argb: C.brand } },
          bottom: { style: 'medium', color: { argb: C.brand } },
          left:   { style: 'thin',   color: { argb: C.border } },
          right:  { style: 'thin',   color: { argb: C.border } },
        };
      }
      totalRow.getCell(2).numFmt = CLP;
      totalRow.getCell(2).alignment = { horizontal: 'right' };

      const utilRow = ws.addRow(['Utilidad Total', Math.floor(totalesGeneralesData.utilidadTotal)]);
      utilRow.height = 24;
      utilRow.font = { name: 'Calibri', bold: true, color: { argb: C.white }, size: 11 };
      utilRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: C.green } };
      for (let i = 1; i <= 2; i++) {
        utilRow.getCell(i).border = {
          top:    { style: 'thin', color: { argb: C.white } },
          bottom: { style: 'thin', color: { argb: C.white } },
          left:   { style: 'thin', color: { argb: C.white } },
          right:  { style: 'thin', color: { argb: C.white } },
        };
      }
      utilRow.getCell(2).numFmt = CLP;
      utilRow.getCell(2).alignment = { horizontal: 'right' };
    }

    // ===== Hoja 2: Reporte Ventas =====
    try {
      // No depender de reporteDiarioData: ese hook ahora solo carga en la pestaña
      // "Reporte de Ventas" (carga por pestaña), y el export debe generar el mismo
      // archivo sin importar desde qué pestaña se exporte. Pide su propia página 0.
      // Una sola llamada con hasta 400 días (el rango máximo permitido es de 12 meses). Antes eran
      // N páginas de 10 días pedidas en paralelo y cada una recalculaba los totales en el servidor.
      // La exportación va con el comprobante de la búsqueda aplicada: no descuenta cupo.
      const diarioCompleto = await reportesRepository.getReporteDiario(fechaDesde, fechaHasta, 0, { tamano: 400, busquedaId });
      const todasFilas = diarioCompleto.items;
      if (todasFilas.length > 0) {
        const ws = wb.addWorksheet('Reporte Ventas');
        const N = 11;
        ws.columns = [
          { key: 'fecha',         width: 14 },
          { key: 'producto',      width: 32 },
          { key: 'origen',        width: 14 },
          { key: 'cantidad',      width: 12 },
          { key: 'efectivo',      width: 16 },
          { key: 'debito',        width: 16 },
          { key: 'credito',       width: 16 },
          { key: 'fiado',         width: 16 },
          { key: 'transferencia', width: 16 },
          { key: 'total',         width: 16 },
          { key: 'utilidad',      width: 16 },
        ];

        const tRow = ws.addRow(['Reporte de Ventas', ...Array(N - 1).fill('')]);
        ws.mergeCells(`A1:K1`);
        styleTitle(tRow, N);

        const hRow = ws.addRow([
          'Fecha', 'Producto', 'Origen', 'Cantidad',
          'Efectivo ($)', 'Débito ($)', 'Crédito ($)', 'Fiado ($)',
          'Transfer. ($)', 'Total ($)', 'Utilidad ($)',
        ]);
        styleHeader(hRow, N);

        // Agrupar por fecha
        const grupos: Record<string, typeof todasFilas> = {};
        todasFilas.forEach(item => {
          if (!grupos[item.fechaVenta]) grupos[item.fechaVenta] = [];
          grupos[item.fechaVenta].push(item);
        });

        let altIdx = 0;
        Object.entries(grupos).forEach(([fecha, items]) => {
          // Cabecera de fecha
          const dateRow = ws.addRow([formatearFechaEspanol(fecha), ...Array(N - 1).fill('')]);
          ws.mergeCells(`A${dateRow.number}:K${dateRow.number}`);
          dateRow.height = 22;
          dateRow.font = { name: 'Calibri', bold: true, color: { argb: C.white }, size: 10 };
          dateRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: C.brand } };
          dateRow.alignment = { horizontal: 'left', vertical: 'middle', indent: 1 };

          items.forEach(item => {
            const row = ws.addRow([
              formatearFechaEspanol(item.fechaVenta),
              formatearNombreProducto(item.nombreProducto),
              item.tipoOrigen,
              item.cantidadTotal,
              Math.round(item.totalEfectivo),
              Math.round(item.totalDebito),
              Math.round(item.totalCredito),
              Math.round(item.totalFiado),
              Math.round(item.totalTransferencia),
              Math.floor(item.totalVentaBruta),
              Math.floor(item.utilidadReal),
            ]);
            styleData(row, N, altIdx % 2 !== 0);
            altIdx++;
            for (let c = 5; c <= N; c++) {
              row.getCell(c).numFmt = CLP;
              row.getCell(c).alignment = { horizontal: 'right' };
            }
            row.getCell(4).alignment = { horizontal: 'right' };
            row.getCell(11).font = { name: 'Calibri', color: { argb: C.green }, bold: true };
          });

          // Fila de total del día
          const totDia = items.reduce(
            (acc, it) => ({
              cant:  acc.cant  + it.cantidadTotal,
              efec:  acc.efec  + it.totalEfectivo,
              deb:   acc.deb   + it.totalDebito,
              cred:  acc.cred  + it.totalCredito,
              fiad:  acc.fiad  + it.totalFiado,
              tran:  acc.tran  + it.totalTransferencia,
              total: acc.total + it.totalVentaBruta,
              util:  acc.util  + it.utilidadReal,
            }),
            { cant: 0, efec: 0, deb: 0, cred: 0, fiad: 0, tran: 0, total: 0, util: 0 }
          );
          const totRow = ws.addRow([
            'TOTAL DÍA', '', '', totDia.cant,
            Math.round(totDia.efec), Math.round(totDia.deb),
            Math.round(totDia.cred), Math.round(totDia.fiad),
            Math.round(totDia.tran), Math.floor(totDia.total),
            Math.floor(totDia.util),
          ]);
          ws.mergeCells(`A${totRow.number}:C${totRow.number}`);
          totRow.height = 22;
          totRow.font = { name: 'Calibri', bold: true, color: { argb: C.dark }, size: 10 };
          totRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: C.greenLight } };
          for (let c = 1; c <= N; c++) {
            totRow.getCell(c).border = {
              top:    { style: 'medium', color: { argb: C.green  } },
              bottom: { style: 'medium', color: { argb: C.green  } },
              left:   { style: 'thin',   color: { argb: C.border } },
              right:  { style: 'thin',   color: { argb: C.border } },
            };
          }
          for (let c = 5; c <= N; c++) {
            totRow.getCell(c).numFmt = CLP;
            totRow.getCell(c).alignment = { horizontal: 'right' };
          }
          totRow.getCell(4).alignment = { horizontal: 'right' };
          totRow.getCell(11).font = { name: 'Calibri', bold: true, color: { argb: C.green }, size: 10 };
        });
      }
    } catch {
      // continuar con las hojas restantes
    }

    // ===== Hoja 3: Detalle Productos =====
    try {
      const allDetalle = await reportesRepository.getDetalleVentasProducto(fechaDesde, fechaHasta, 0, 9999, { busquedaId });
      if (allDetalle.items.length > 0) {
        const ws = wb.addWorksheet('Detalle Productos');
        const N = 7;
        ws.columns = [
          { key: 'producto',  width: 32 },
          { key: 'categoria', width: 22 },
          { key: 'cantidad',  width: 14 },
          { key: 'pCompra',   width: 17 },
          { key: 'pVenta',    width: 17 },
          { key: 'utilUnit',  width: 19 },
          { key: 'utilTotal', width: 19 },
        ];

        const tRow = ws.addRow(['Detalle de Ventas por Producto', ...Array(N - 1).fill('')]);
        ws.mergeCells('A1:G1');
        styleTitle(tRow, N);

        const hRow = ws.addRow([
          'Producto', 'Categoría', 'Cantidad',
          'P. Compra ($)', 'P. Venta ($)', 'Utilidad Unit. ($)', 'Utilidad Total ($)',
        ]);
        styleHeader(hRow, N);

        allDetalle.items.forEach((item, idx) => {
          const row = ws.addRow([
            formatearNombreProducto(item.nombreProducto),
            formatearNombreProducto(item.categoria),
            item.cantidadVendida,
            Math.floor(item.precioCompra),
            Math.floor(item.precioVenta),
            Math.floor(item.utilidad),
            Math.floor(item.utilidadTotal),
          ]);
          styleData(row, N, idx % 2 !== 0);
          for (let c = 4; c <= N; c++) {
            row.getCell(c).numFmt = CLP;
            row.getCell(c).alignment = { horizontal: 'right' };
          }
          row.getCell(3).alignment = { horizontal: 'right' };
          row.getCell(7).font = { name: 'Calibri', bold: true, color: { argb: C.green } };
        });
      }
    } catch {
      // continuar con las hojas restantes
    }

    // ===== Hoja 4: Por Categoría =====
    try {
      // Solo premium: al plan gratuito el backend le responde 403 a este endpoint, así que ni se pide.
      const allCategorias = hasPremiumAccess
        ? await reportesRepository.getDetalleVentasCategoriaAlmacen(fechaDesde, fechaHasta)
        : [];
      if (allCategorias.length > 0) {
        const ws = wb.addWorksheet('Por Categoría');
        const N = 5;
        ws.columns = [
          { key: 'categoria',  width: 32 },
          { key: 'tipo',       width: 16 },
          { key: 'cantidad',   width: 18 },
          { key: 'totalVenta', width: 19 },
          { key: 'utilidad',   width: 19 },
        ];

        const tRow = ws.addRow(['Detalle de Ventas por Categoría', ...Array(N - 1).fill('')]);
        ws.mergeCells('A1:E1');
        styleTitle(tRow, N);

        const hRow = ws.addRow(['Categoría', 'Tipo', 'Cantidad Vendida', 'Total Venta', 'Utilidad Total']);
        styleHeader(hRow, N);

        allCategorias.forEach((item, idx) => {
          const tipoLabel = item.tipoCategoria === 'PROPIA' ? 'Propia' : item.tipoCategoria === 'GLOBAL' ? 'Global' : '';
          const row = ws.addRow([
            item.categoriaNombre,
            tipoLabel,
            item.cantidadVendida,
            Math.floor(item.totalVenta),
            Math.floor(item.utilidadTotal),
          ]);
          styleData(row, N, idx % 2 !== 0);
          for (let c = 4; c <= N; c++) {
            row.getCell(c).numFmt = CLP;
            row.getCell(c).alignment = { horizontal: 'right' };
          }
          row.getCell(3).alignment = { horizontal: 'right' };
          row.getCell(5).font = { name: 'Calibri', bold: true, color: { argb: C.green } };
        });
      }
    } catch {
      // continuar con las hojas restantes
    }

    // ===== Hoja 5: Alertas Inventario =====
    if (alertasData && alertasData.length > 0) {
      const ws = wb.addWorksheet('Alertas Inventario');
      const N = 5;
      ws.columns = [
        { key: 'codigo',      width: 18 },
        { key: 'producto',    width: 32 },
        { key: 'vencimiento', width: 20 },
        { key: 'stock',       width: 12 },
        { key: 'estado',      width: 16 },
      ];

      const tRow = ws.addRow(['Alertas de Inventario', '', '', '', '']);
      ws.mergeCells('A1:E1');
      styleTitle(tRow, N, C.red);

      const hRow = ws.addRow(['Código', 'Producto', 'Fecha Vencimiento', 'Stock', 'Estado']);
      styleHeader(hRow, N);

      alertasData.forEach(alerta => {
        const isVencido = alerta.estado === 'VENCIDO';
        const row = ws.addRow([
          alerta.codigo ?? '',
          formatearNombreProducto(alerta.nombreProducto),
          formatearFechaEspanol(alerta.fechaVencimiento),
          alerta.stock,
          isVencido ? 'Vencido' : 'Por Vencer',
        ]);
        row.height = 22;
        row.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: isVencido ? C.redLight : C.orangeLight } };
        row.getCell(1).font = { name: 'Calibri', color: { argb: 'FF6B7280' } };
        row.getCell(5).font = { name: 'Calibri', bold: true, color: { argb: isVencido ? C.red : C.orange } };
        for (let c = 1; c <= N; c++) {
          row.getCell(c).border = {
            bottom: { style: 'thin', color: { argb: isVencido ? 'FFFCA5A5' : 'FFFCD34D' } },
            left:   { style: 'thin', color: { argb: C.border } },
            right:  { style: 'thin', color: { argb: C.border } },
          };
        }
        row.getCell(4).alignment = { horizontal: 'center' };
        row.getCell(5).alignment = { horizontal: 'center' };
      });
    }

    if (wb.worksheets.length === 0) {
      addToast('error', 'No hay datos para exportar');
      return;
    }

    const buffer = await wb.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `reporte-${fechaDesde}-${fechaHasta}.xlsx`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleExportar = async () => {
    setExportando(true);
    try {
      await exportarExcel();
    } catch {
      addToast('error', 'Error al exportar el reporte');
    } finally {
      setExportando(false);
    }
  };

  // Validar fechas (sobre el rango en edición: fechaDesdeInput/fechaHastaInput)
  const validarFechas = (): boolean => {
    if (!fechaDesdeInput || !fechaHastaInput) {
      addToast('error', 'Debes seleccionar ambas fechas');
      return false;
    }

    const desde = new Date(fechaDesdeInput);
    const hasta = new Date(fechaHastaInput);
    const limite = new Date(getFechaLimite());
    const hoy = new Date(getFechaMaxima());

    if (desde < limite) {
      addToast(
        'error',
        hasPremiumAccess
          ? 'La fecha desde no puede ser mayor a 1 año atrás'
          : `Con el plan gratuito solo puedes consultar los últimos ${DIAS_MAX_FREE} días`
      );
      return false;
    }

    if (hasta > hoy) {
      addToast('error', 'La fecha hasta no puede ser mayor a hoy');
      return false;
    }

    if (hasta < desde) {
      addToast('error', 'La fecha hasta no puede ser menor a la fecha desde');
      return false;
    }

    return true;
  };

  // Manejar búsqueda: solo se dispara con el clic explícito en "Buscar" (ya no hay
  // autobúsqueda al cambiar las fechas). Primero registra la búsqueda en el backend (el único
  // paso que descuenta del cupo diario del plan gratuito y que entrega el comprobante); recién
  // con ese comprobante copia el rango en edición al rango aplicado, que es el que alimentan
  // los hooks de datos y usa la exportación a Excel. Si el backend rechaza la búsqueda, los
  // resultados anteriores se mantienen tal cual.
  const handleBuscar = async () => {
    if (!validarFechas()) return;
    // Corta cualquier oleada anterior que siga viva (p. ej. si el usuario buscó de
    // nuevo antes de que terminara la carga previa).
    await queryClient.cancelQueries({ queryKey: ['reportes'] });
    try {
      const busqueda = await iniciarBusqueda({ desde: fechaDesdeInput, hasta: fechaHastaInput });
      setBusquedaId(busqueda.busquedaId);
      setCupo(busqueda.premium ? null : { limiteDiario: busqueda.limiteDiario, restantes: busqueda.restantes });
      setFechaDesde(fechaDesdeInput);
      setFechaHasta(fechaHastaInput);
      setBusquedaRealizada(true);
      setPaginaActual(0);
      setPaginaDetalleProducto(0); // antes no se reseteaba al rebuscar
    } catch (error) {
      if ((error as ApiError)?.code === 'LIMITE_REPORTES_DIARIO_EXCEDIDO') {
        setCupo((anterior) => ({ limiteDiario: anterior?.limiteDiario ?? null, restantes: 0 }));
      }
      addToast('error', mensajeDeError(error));
    }
  };

  // Hooks de datos (solo se ejecutan si busquedaRealizada es true)
  const { data: recaudacionData, isLoading: loadingRecaudacion, error: errorRecaudacion } = useRecaudacionChart(
    fechaDesde,
    fechaHasta,
    busquedaRealizada,
    busquedaId
  );

  const { data: topProductosData, isLoading: loadingTopProductos, error: errorTopProductos } = useTopProductos(
    fechaDesde,
    fechaHasta,
    busquedaRealizada,
    busquedaId
  );

  const { data: utilidadCostoData, isLoading: loadingUtilidadCosto, error: errorUtilidadCosto } = useUtilidadCosto(
    fechaDesde,
    fechaHasta,
    busquedaRealizada,
    busquedaId
  );

  // Los 4 hooks siguientes solo cargan cuando su pestaña está activa: antes las 8
  // consultas salían juntas en cada búsqueda (recaudación, top-productos, utilidad-costo
  // y totales siguen siendo siempre visibles, así que esos 4 no se tocan). Reduce la
  // primera carga de 8 a 5 peticiones.
  const { data: reporteDiarioData, isLoading: loadingReporteDiario, error: errorReporteDiario } = useReporteDiario(
    fechaDesde,
    fechaHasta,
    paginaActual,
    busquedaRealizada && tabActiva === 'ventas',
    busquedaId
  );

  const { data: alertasData, isLoading: loadingAlertas } = useAlertasInventario(
    (busquedaRealizada || vieneDeNavegacion) && tabActiva === 'alertas'
  );

  const { data: totalesGeneralesData, isLoading: loadingTotalesGenerales, error: errorTotalesGenerales } = useTotalesGenerales(
    fechaDesde,
    fechaHasta,
    busquedaRealizada,
    busquedaId
  );

  const { data: detalleProductoData, isLoading: loadingDetalleProducto, error: errorDetalleProducto } = useDetalleVentasProducto(
    fechaDesde,
    fechaHasta,
    paginaDetalleProducto,
    10,
    busquedaRealizada && tabActiva === 'detalle-producto',
    busquedaId
  );

  // + hasPremiumAccess: hoy esta consulta salía igual para cuentas sin premium y el
  // backend la rechazaba (el paywall de más abajo es solo de render) -- se elimina esa
  // petición desperdiciada; para cuentas premium no cambia nada.
  const { data: detalleCategoriaData, isLoading: loadingDetalleCategoria } = useDetalleVentasCategoriaAlmacen(
    fechaDesde,
    fechaHasta,
    busquedaRealizada && tabActiva === 'por-categoria' && hasPremiumAccess
  );

  // true mientras cualquiera de las 8 consultas de reportes está cargando (aunque solo
  // 5 estén activas a la vez, por la carga por pestaña). Bloquea los inputs de fecha,
  // el botón "Buscar", las pestañas y el botón de exportar mientras dura.
  // + iniciandoBusqueda: el POST que registra la búsqueda también bloquea el botón, para que
  // un doble clic no la repita.
  const cargando = loadingRecaudacion || loadingTopProductos || loadingUtilidadCosto
    || loadingReporteDiario || loadingTotalesGenerales || loadingDetalleProducto
    || loadingDetalleCategoria || loadingAlertas || iniciandoBusqueda;

  // La búsqueda puede vencer con la pantalla abierta (cambió el día, o el comprobante ya no
  // corresponde): el backend responde 409 BUSQUEDA_REPORTE_INVALIDA a la consulta de la
  // pestaña. Se avisa y se vuelve al estado previo a buscar, para que el usuario presione
  // Buscar de nuevo (si el rango ya se buscó hace poco, el backend lo reutiliza sin gastar cupo).
  const errorBusquedaVencida = [
    errorRecaudacion, errorTopProductos, errorUtilidadCosto,
    errorReporteDiario, errorTotalesGenerales, errorDetalleProducto,
  ].find((e) => (e as ApiError | null)?.code === 'BUSQUEDA_REPORTE_INVALIDA') as ApiError | undefined;

  useEffect(() => {
    if (!errorBusquedaVencida) return;
    addToast('error', errorBusquedaVencida.message);
    setBusquedaRealizada(false);
    setBusquedaId(undefined);
  }, [errorBusquedaVencida, addToast]);

  // Agrupar items por fecha
  const itemsAgrupados = reporteDiarioData?.items.reduce((acc, item) => {
    if (!acc[item.fechaVenta]) {
      acc[item.fechaVenta] = [];
    }
    acc[item.fechaVenta].push(item);
    return acc;
  }, {} as Record<string, typeof reporteDiarioData.items>) || {};

  // Formatear datos para gráfico de barras
  const barChartData = utilidadCostoData
    ? [
        {
          nombre: 'Venta Total',
          monto: Math.floor(utilidadCostoData.ventaTotal),
          color: '#43BFFF',
        },
        {
          nombre: 'Venta Calculadora',
          monto: Math.floor(utilidadCostoData.ventaCalculadora),
          color: '#8B5CF6',
        },
        {
          nombre: 'Venta Inventario',
          monto: Math.floor(utilidadCostoData.ventaInventario),
          color: '#FFB84D',
        },
        {
          nombre: 'Costo',
          monto: Math.floor(utilidadCostoData.costo),
          color: '#EF4444',
        },
        {
          nombre: 'Utilidad',
          monto: Math.floor(utilidadCostoData.utilidad),
          color: '#22C55E',
        },
      ]
    : [];

  const alertasOrdenadas = useMemo(() => {
    if (!alertasData || !alertasSort) return alertasData ?? [];
    return [...alertasData].sort((a, b) => {
      const { campo, dir } = alertasSort;
      const valA = a[campo] ?? '';
      const valB = b[campo] ?? '';
      let cmp = 0;
      if (typeof valA === 'number' && typeof valB === 'number') {
        cmp = valA - valB;
      } else {
        cmp = String(valA).localeCompare(String(valB), 'es');
      }
      return dir === 'asc' ? cmp : -cmp;
    });
  }, [alertasData, alertasSort]);

  const toggleSort = (campo: NonNullable<typeof alertasSort>['campo']) => {
    setAlertasSort(prev =>
      prev?.campo === campo
        ? { campo, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { campo, dir: 'asc' }
    );
  };

  return (
    <div className="p-4 md:p-6 lg:p-8">
      <div className="mb-6">
        <h1 className="text-3xl font-bold" style={{ color: '#11194F' }}>
          Reportes
        </h1>
        <p className="text-gray-600 mt-2">
          Analiza el rendimiento de tu negocio con nuestros reportes detallados
        </p>
      </div>

      {/* Filtros de fecha */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
        <h2 className="text-lg font-semibold mb-4" style={{ color: '#11194F' }}>
          Filtrar por fecha
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Desde
            </label>
            <input
              type="date"
              value={fechaDesdeInput}
              onChange={(e) => setFechaDesdeInput(e.target.value)}
              onClick={(e) => {
                try {
                  (e.target as HTMLInputElement).showPicker?.();
                } catch (error) {
                  // Navegador no soporta showPicker
                }
              }}
              min={getFechaLimite()}
              max={getFechaMaxima()}
              disabled={cargando}
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:border-blue-500 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Hasta
            </label>
            <input
              type="date"
              value={fechaHastaInput}
              onChange={(e) => setFechaHastaInput(e.target.value)}
              onClick={(e) => {
                try {
                  (e.target as HTMLInputElement).showPicker?.();
                } catch (error) {
                  // Navegador no soporta showPicker
                }
              }}
              min={getFechaLimite()}
              max={getFechaMaxima()}
              disabled={cargando}
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:border-blue-500 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            />
          </div>
        </div>
        <div className="flex justify-end mt-4">
          <button
            onClick={handleBuscar}
            disabled={cargando}
            className="flex items-center gap-2 px-6 py-3 text-white font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ backgroundColor: '#43BFFF' }}
          >
            <MdSearch className="text-xl" />
            {cargando ? 'Buscando...' : 'Buscar'}
          </button>
        </div>
        <p className="text-xs text-gray-500 mt-3">
          * Puedes consultar información desde hace {hasPremiumAccess ? '1 año' : `${DIAS_MAX_FREE} días`} hasta hoy. Presiona <strong>Buscar</strong> para ejecutar la consulta. Rangos más cortos se cargan más rápido.
        </p>
        <div className="mt-3 rounded-lg bg-blue-50 px-4 py-3 text-sm" style={{ color: '#11194F' }}>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
            <FaCrown className="text-purple-600 shrink-0" />
            <span>
              {hasPremiumAccess ? (
                <>
                  <strong>Plan Premium:</strong> búsquedas sin límite y hasta 1 año de historial.
                </>
              ) : (
                <>
                  {cupo && cupo.restantes === 0 ? (
                    <strong>Alcanzaste el límite de búsquedas de hoy. Podrás volver a buscar mañana.</strong>
                  ) : cupo && cupo.limiteDiario != null && cupo.restantes != null ? (
                    <strong>Te quedan {cupo.restantes} de {cupo.limiteDiario} búsquedas hoy.</strong>
                  ) : (
                    <>Plan gratuito: las búsquedas diarias son limitadas.</>
                  )}{' '}
                  Con Premium consultas sin límite y con hasta 1 año de historial.
                </>
              )}
            </span>
            <div className="ml-auto flex items-center gap-4">
              <button
                type="button"
                onClick={() => setMostrarPlanes((visible) => !visible)}
                aria-expanded={mostrarPlanes}
                className="font-semibold underline"
              >
                {mostrarPlanes ? 'Ocultar comparación' : 'Ver qué incluye cada plan'}
              </button>
              {!hasPremiumAccess && (
                <button type="button" onClick={() => goToPremium()} className="font-semibold underline">
                  Ver Premium
                </button>
              )}
            </div>
          </div>

          {mostrarPlanes && (
            <div className="mt-3 overflow-x-auto rounded-lg bg-white border border-blue-100">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-blue-100">
                    <th className="px-4 py-2 font-semibold">¿Qué puedes hacer?</th>
                    <th className="px-4 py-2 font-semibold">
                      Plan gratuito{!hasPremiumAccess && <span className="ml-1 text-xs font-normal text-gray-500">(tu plan)</span>}
                    </th>
                    <th className="px-4 py-2 font-semibold">
                      Premium{hasPremiumAccess && <span className="ml-1 text-xs font-normal text-gray-500">(tu plan)</span>}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-blue-50">
                    <td className="px-4 py-2">Historial que puedes consultar</td>
                    <td className="px-4 py-2">Los últimos {DIAS_MAX_FREE} días</td>
                    <td className="px-4 py-2">Hasta 1 año atrás</td>
                  </tr>
                  <tr className="border-b border-blue-50">
                    <td className="px-4 py-2">Búsquedas por día (por sucursal)</td>
                    <td className="px-4 py-2">
                      {cupo?.limiteDiario != null ? `Hasta ${cupo.limiteDiario}` : 'Limitadas'}
                    </td>
                    <td className="px-4 py-2">Sin límite</td>
                  </tr>
                  <tr className="border-b border-blue-50">
                    <td className="px-4 py-2">Gráficos, totales y reporte de ventas</td>
                    <td className="px-4 py-2">Incluido</td>
                    <td className="px-4 py-2">Incluido</td>
                  </tr>
                  <tr className="border-b border-blue-50">
                    <td className="px-4 py-2">Detalle de ventas por producto</td>
                    <td className="px-4 py-2">Incluido</td>
                    <td className="px-4 py-2">Incluido</td>
                  </tr>
                  <tr className="border-b border-blue-50">
                    <td className="px-4 py-2">Alertas de inventario</td>
                    <td className="px-4 py-2">Incluido (no cuenta como búsqueda)</td>
                    <td className="px-4 py-2">Incluido</td>
                  </tr>
                  <tr className="border-b border-blue-50">
                    <td className="px-4 py-2">Ventas por categoría</td>
                    <td className="px-4 py-2">No incluido</td>
                    <td className="px-4 py-2">Incluido</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2">Exportar a Excel</td>
                    <td className="px-4 py-2">Incluido, sin la hoja de categorías</td>
                    <td className="px-4 py-2">Incluido, completo</td>
                  </tr>
                </tbody>
              </table>
              <p className="px-4 py-2 text-xs text-gray-500">
                Una búsqueda es un clic en <strong>Buscar</strong>. Cambiar de pestaña, paginar o exportar no gasta búsquedas,
                y repetir la misma búsqueda en pocos minutos tampoco.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Mostrar resultados solo si se realizó la búsqueda */}
      {busquedaRealizada && (
        <>
          {/* Botón exportar */}
          <div className="flex justify-end mb-4">
            <button
              onClick={handleExportar}
              disabled={exportando || cargando}
              className="flex items-center gap-2 px-5 py-2.5 text-white font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ backgroundColor: exportando ? '#16a34a' : '#22c55e' }}
            >
              <MdDownload className="text-xl" />
              {exportando ? 'Exportando...' : 'Exportar Excel'}
            </button>
          </div>

          {/* Sección de Gráficos */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold mb-4" style={{ color: '#11194F' }}>
              Análisis Gráfico
            </h2>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              {/* Gráfico de Recaudación */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold mb-4" style={{ color: '#11194F' }}>
                  Recaudación por Día
                </h3>
                {loadingRecaudacion ? (
                  <div className="h-80 flex items-center justify-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-4 border-gray-200 border-t-blue-500"></div>
                  </div>
                ) : recaudacionData && recaudacionData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={recaudacionData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="dia" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Line
                        type="monotone"
                        dataKey="total"
                        stroke="#43BFFF"
                        strokeWidth={3}
                        name="Total Recaudado"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-center text-gray-500 py-12">No hay datos disponibles</p>
                )}
              </div>

              {/* Gráfico de Top Productos */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold mb-4" style={{ color: '#11194F' }}>
                  Top 10 Productos Más Vendidos
                </h3>
                {loadingTopProductos ? (
                  <div className="h-80 flex items-center justify-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-4 border-gray-200 border-t-blue-500"></div>
                  </div>
                ) : topProductosData && topProductosData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={400}>
                    <BarChart
                      data={topProductosData.map(item => ({
                        ...item,
                        nombreProductoTruncado: truncarNombre(formatearNombreProducto(item.nombreProducto), 20)
                      }))}
                      layout="vertical"
                      margin={{ top: 5, right: 60, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" />
                      <YAxis 
                        type="category" 
                        dataKey="nombreProductoTruncado" 
                        width={160}
                        tick={{ fontSize: 12 }}
                      />
                      <Tooltip 
                        formatter={(value) => [value ?? 0, 'Cantidad']}
                        labelFormatter={(label) => {
                          const labelStr = String(label ?? '');
                          const item = topProductosData.find(p => truncarNombre(formatearNombreProducto(p.nombreProducto), 20) === labelStr);
                          return item ? formatearNombreProducto(item.nombreProducto) : labelStr;
                        }}
                      />
                      <Legend />
                      <Bar dataKey="cantidadVendida" fill="#43BFFF" name="Cantidad Vendida">
                        {topProductosData.map((_, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                        <LabelList dataKey="cantidadVendida" position="right" />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-center text-gray-500 py-12">No hay datos disponibles</p>
                )}
              </div>
            </div>

            {/* Gráfico de Venta, Costo y Utilidad */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold mb-4" style={{ color: '#11194F' }}>
                Venta, Costo y Utilidad
              </h3>
              {loadingUtilidadCosto ? (
                <div className="h-80 flex items-center justify-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-4 border-gray-200 border-t-blue-500"></div>
                </div>
              ) : utilidadCostoData ? (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-600">Venta Total</p>
                      <p className="text-2xl font-bold" style={{ color: '#43BFFF' }}>
                        ${Math.floor(utilidadCostoData.ventaTotal).toLocaleString('es-CL')}
                      </p>
                    </div>
                    <div className="bg-purple-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-600">Venta Calculadora</p>
                      <p className="text-2xl font-bold" style={{ color: '#8B5CF6' }}>
                        ${Math.floor(utilidadCostoData.ventaCalculadora).toLocaleString('es-CL')}
                      </p>
                    </div>
                    <div className="bg-orange-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-600">Venta Inventario</p>
                      <p className="text-2xl font-bold" style={{ color: '#FFB84D' }}>
                        ${Math.floor(utilidadCostoData.ventaInventario).toLocaleString('es-CL')}
                      </p>
                    </div>
                    <div className="bg-red-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-600">Costo</p>
                      <p className="text-2xl font-bold" style={{ color: '#EF4444' }}>
                        ${Math.floor(utilidadCostoData.costo).toLocaleString('es-CL')}
                      </p>
                    </div>
                    <div className="bg-green-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-600">Utilidad</p>
                      <p className="text-2xl font-bold" style={{ color: '#22C55E' }}>
                        ${Math.floor(utilidadCostoData.utilidad).toLocaleString('es-CL')}
                      </p>
                    </div>
                  </div>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={barChartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="nombre" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="monto" fill="#43BFFF" name="Monto ($)">
                        {barChartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </>
              ) : (
                <p className="text-center text-gray-500 py-12">No hay datos disponibles</p>
              )}
            </div>
          </div>

          {/* Totales Generales - Siempre visible */}
          {loadingTotalesGenerales ? (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 mb-6">
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-gray-200 border-t-blue-500"></div>
              </div>
            </div>
          ) : totalesGeneralesData ? (
            <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl shadow-lg p-6 mb-6 text-white">
              <h3 className="text-xl font-bold mb-4">Totales Generales</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                <div>
                  <p className="text-sm opacity-90">Efectivo</p>
                  <p className="text-2xl font-bold">
                    ${Math.round(totalesGeneralesData.totalEfectivo).toLocaleString('es-CL')}
                  </p>
                </div>
                <div>
                  <p className="text-sm opacity-90">Débito</p>
                  <p className="text-2xl font-bold">
                    ${Math.round(totalesGeneralesData.totalDebito).toLocaleString('es-CL')}
                  </p>
                </div>
                <div>
                  <p className="text-sm opacity-90">Crédito</p>
                  <p className="text-2xl font-bold">
                    ${Math.round(totalesGeneralesData.totalCredito).toLocaleString('es-CL')}
                  </p>
                </div>
                <div>
                  <p className="text-sm opacity-90">Fiado</p>
                  <p className="text-2xl font-bold">
                    ${Math.round(totalesGeneralesData.totalFiado).toLocaleString('es-CL')}
                  </p>
                </div>
                <div>
                  <p className="text-sm opacity-90">Transferencia</p>
                  <p className="text-2xl font-bold">
                    ${Math.round(totalesGeneralesData.totalTransferencia).toLocaleString('es-CL')}
                  </p>
                </div>
                <div className="bg-white bg-opacity-20 rounded-lg p-3">
                  <p className="text-sm opacity-90">Total Venta</p>
                  <p className="text-2xl font-bold">
                    ${Math.floor(totalesGeneralesData.totalVenta).toLocaleString('es-CL')}
                  </p>
                  <p className="text-xs opacity-75 mt-1">
                    Utilidad: ${Math.floor(totalesGeneralesData.utilidadTotal).toLocaleString('es-CL')}
                  </p>
                </div>
              </div>
            </div>
          ) : null}
        </>
      )}

      {/* Sección de Reportes con Pestañas — visible también si viene del dashboard */}
      {(busquedaRealizada || vieneDeNavegacion) && (
        <div className="mb-8">
            {/* Pestañas */}
            <div className="flex gap-2 mb-6 border-b-2 border-gray-200">
              <button
                onClick={() => setTabActiva('ventas')}
                disabled={cargando}
                className={`px-6 py-3 font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                  tabActiva === 'ventas'
                    ? 'border-b-4 border-blue-500 text-blue-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
                style={tabActiva === 'ventas' ? { color: '#11194F', borderColor: '#43BFFF' } : {}}
              >
                Reporte de Ventas
              </button>
              <button
                onClick={() => setTabActiva('detalle-producto')}
                disabled={cargando}
                className={`px-6 py-3 font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                  tabActiva === 'detalle-producto'
                    ? 'border-b-4 border-purple-500 text-purple-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
                style={tabActiva === 'detalle-producto' ? { color: '#11194F', borderColor: '#8B5CF6' } : {}}
              >
                Detalle por Producto
              </button>
              <button
                onClick={() => setTabActiva('por-categoria')}
                disabled={cargando}
                className={`px-6 py-3 font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                  tabActiva === 'por-categoria'
                    ? 'border-b-4 border-green-500 text-green-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
                style={tabActiva === 'por-categoria' ? { color: '#11194F', borderColor: '#22C55E' } : {}}
              >
                Por Categoría
              </button>
              <button
                onClick={() => setTabActiva('alertas')}
                disabled={cargando}
                className={`px-6 py-3 font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                  tabActiva === 'alertas'
                    ? 'border-b-4 border-red-500 text-red-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
                style={tabActiva === 'alertas' ? { color: '#11194F', borderColor: '#EF4444' } : {}}
              >
                Alertas de Inventario
              </button>
            </div>

            {/* Contenido de Reporte de Ventas */}
            {tabActiva === 'ventas' && (
              <div>
                {!busquedaRealizada ? (
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
                    <MdSearch className="text-6xl text-gray-300 mx-auto mb-4" />
                    <p className="text-xl text-gray-600">Selecciona un rango de fechas para ver el reporte de ventas</p>
                  </div>
                ) : loadingReporteDiario ? (
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12">
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-12 w-12 border-4 border-gray-200 border-t-blue-500"></div>
                    </div>
                  </div>
                ) : reporteDiarioData && Object.keys(itemsAgrupados).length > 0 ? (
                  <>
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                      {Object.entries(itemsAgrupados).map(([fecha, items]) => {
                        // Calcular totales para esta fecha
                        const totalesFecha = items.reduce(
                          (acc, item) => ({
                            cantidadTotal: acc.cantidadTotal + item.cantidadTotal,
                            totalEfectivo: acc.totalEfectivo + item.totalEfectivo,
                            totalDebito: acc.totalDebito + item.totalDebito,
                            totalCredito: acc.totalCredito + item.totalCredito,
                            totalFiado: acc.totalFiado + item.totalFiado,
                            totalTransferencia: acc.totalTransferencia + item.totalTransferencia,
                            totalVentaBruta: acc.totalVentaBruta + item.totalVentaBruta,
                            utilidadReal: acc.utilidadReal + item.utilidadReal,
                          }),
                          {
                            cantidadTotal: 0,
                            totalEfectivo: 0,
                            totalDebito: 0,
                            totalCredito: 0,
                            totalFiado: 0,
                            totalTransferencia: 0,
                            totalVentaBruta: 0,
                            utilidadReal: 0,
                          }
                        );

                        return (
                          <div key={fecha} className="border-b last:border-b-0">
                            {/* Encabezado de Fecha */}
                            <div className="bg-gradient-to-r from-blue-500 to-blue-600 px-6 py-4 border-b-4 border-blue-700">
                              <h4 className="font-bold text-xl text-white flex items-center gap-2">
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                                {formatearFechaEspanol(fecha)}
                              </h4>
                            </div>

                            {/* Tabla de productos de ese día */}
                            <div className="overflow-x-auto">
                              <table className="w-full">
                                <thead className="bg-gray-100">
                                  <tr>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Producto</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Origen</th>
                                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">Cant.</th>
                                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">Efectivo</th>
                                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">Débito</th>
                                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">Crédito</th>
                                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">Fiado</th>
                                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">Transfer.</th>
                                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">Total</th>
                                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">Utilidad</th>
                                  </tr>
                                </thead>
                                <tbody>
                                {items.map((item, idx) => (
                                  <tr key={idx} className="border-t hover:bg-gray-50">
                                    <td className="px-4 py-3 text-sm">{formatearNombreProducto(item.nombreProducto)}</td>
                                    <td className="px-4 py-3 text-sm">
                                      <span
                                        className={`px-2 py-1 rounded text-xs font-medium ${
                                          item.tipoOrigen === 'CATALOGO'
                                            ? 'bg-green-100 text-green-800'
                                            : 'bg-orange-100 text-orange-800'
                                        }`}
                                      >
                                        {item.tipoOrigen}
                                      </span>
                                    </td>
                                    <td className="px-4 py-3 text-sm text-right">{formatearCantidad(item.cantidadTotal)}</td>
                                    <td className="px-4 py-3 text-sm text-right">
                                      ${Math.round(item.totalEfectivo).toLocaleString('es-CL')}
                                    </td>
                                    <td className="px-4 py-3 text-sm text-right">
                                      ${Math.round(item.totalDebito).toLocaleString('es-CL')}
                                    </td>
                                    <td className="px-4 py-3 text-sm text-right">
                                      ${Math.round(item.totalCredito).toLocaleString('es-CL')}
                                    </td>
                                    <td className="px-4 py-3 text-sm text-right">
                                      ${Math.round(item.totalFiado).toLocaleString('es-CL')}
                                    </td>
                                    <td className="px-4 py-3 text-sm text-right">
                                      ${Math.round(item.totalTransferencia).toLocaleString('es-CL')}
                                    </td>
                                    <td className="px-4 py-3 text-sm text-right font-semibold">
                                      ${Math.floor(item.totalVentaBruta).toLocaleString('es-CL')}
                                    </td>
                                    <td className="px-4 py-3 text-sm text-right font-semibold text-green-600">
                                      ${Math.floor(item.utilidadReal).toLocaleString('es-CL')}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                              <tfoot className="bg-gradient-to-r from-green-50 to-green-100 border-t-2 border-green-500">
                                <tr>
                                  <td className="px-4 py-4 text-sm font-bold text-gray-700" colSpan={2}>
                                    TOTAL DEL DÍA
                                  </td>
                                  <td className="px-4 py-4 text-sm font-bold text-gray-900 text-right">
                                    {formatearCantidad(totalesFecha.cantidadTotal)}
                                  </td>
                                  <td className="px-4 py-4 text-sm font-bold text-gray-900 text-right">
                                    ${Math.round(totalesFecha.totalEfectivo).toLocaleString('es-CL')}
                                  </td>
                                  <td className="px-4 py-4 text-sm font-bold text-gray-900 text-right">
                                    ${Math.round(totalesFecha.totalDebito).toLocaleString('es-CL')}
                                  </td>
                                  <td className="px-4 py-4 text-sm font-bold text-gray-900 text-right">
                                    ${Math.round(totalesFecha.totalCredito).toLocaleString('es-CL')}
                                  </td>
                                  <td className="px-4 py-4 text-sm font-bold text-gray-900 text-right">
                                    ${Math.round(totalesFecha.totalFiado).toLocaleString('es-CL')}
                                  </td>
                                  <td className="px-4 py-4 text-sm font-bold text-gray-900 text-right">
                                    ${Math.round(totalesFecha.totalTransferencia).toLocaleString('es-CL')}
                                  </td>
                                  <td className="px-4 py-4 text-sm font-bold text-green-700 text-right">
                                    ${Math.floor(totalesFecha.totalVentaBruta).toLocaleString('es-CL')}
                                  </td>
                                  <td className="px-4 py-4 text-sm font-bold text-green-700 text-right">
                                    ${Math.floor(totalesFecha.utilidadReal).toLocaleString('es-CL')}
                                  </td>
                                </tr>
                              </tfoot>
                            </table>
                          </div>
                        </div>
                        );
                      })}
                    </div>

                    {/* Paginación */}
                    {reporteDiarioData.totalPaginas > 1 && (
                      <div className="mt-6 flex justify-center gap-2">
                        <button
                          onClick={() => setPaginaActual(Math.max(0, paginaActual - 1))}
                          disabled={paginaActual === 0}
                          className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Anterior
                        </button>
                        <span className="px-4 py-2 bg-blue-500 text-white rounded-lg font-semibold">
                          Página {paginaActual + 1} de {reporteDiarioData.totalPaginas}
                        </span>
                        <button
                          onClick={() =>
                            setPaginaActual(Math.min(reporteDiarioData.totalPaginas - 1, paginaActual + 1))
                          }
                          disabled={paginaActual >= reporteDiarioData.totalPaginas - 1}
                          className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Siguiente
                        </button>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12">
                    <p className="text-center text-gray-500">No hay datos disponibles</p>
                  </div>
                )}
              </div>
            )}

            {/* Contenido de Por Categoría */}
            {tabActiva === 'por-categoria' && (
              <div>
                {!hasPremiumAccess ? (
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
                    <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-gradient-to-r from-purple-100 to-pink-100 mb-6">
                      <FaCrown className="text-5xl text-purple-600" />
                    </div>
                    <h3 className="text-2xl font-bold mb-3" style={{ color: '#11194F' }}>Función Premium</h3>
                    <p className="text-gray-600 mb-6 max-w-md mx-auto">
                      Disponible con suscripción Premium
                    </p>
                    <button
                      onClick={() => goToPremium()}
                      className="px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg font-bold shadow-lg hover:shadow-xl transition-all hover:scale-105"
                    >
                      Ver Planes
                    </button>
                  </div>
                ) : loadingDetalleCategoria ? (
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12">
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-12 w-12 border-4 border-gray-200 border-t-green-500"></div>
                    </div>
                  </div>
                ) : detalleCategoriaData && detalleCategoriaData.length > 0 ? (
                  <>
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
                      <h3 className="text-lg font-semibold mb-4" style={{ color: '#11194F' }}>
                        Ventas por Categoría
                      </h3>
                      <ResponsiveContainer width="100%" height={Math.max(200, detalleCategoriaData.length * 45)}>
                        <BarChart
                          data={detalleCategoriaData.map(item => ({
                            ...item,
                            categoriaNombreTruncado: truncarNombre(item.categoriaNombre, 20),
                          }))}
                          layout="vertical"
                          margin={{ top: 5, right: 60, left: 20, bottom: 5 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis type="number" />
                          <YAxis
                            type="category"
                            dataKey="categoriaNombreTruncado"
                            width={160}
                            tick={{ fontSize: 12 }}
                          />
                          <Tooltip
                            formatter={(value) => {
                              const val = typeof value === 'number' ? value : 0;
                              return [`$${Math.floor(val).toLocaleString('es-CL')}`, 'Total Venta'];
                            }}
                            labelFormatter={(label) => {
                              const labelStr = String(label ?? '');
                              const item = detalleCategoriaData.find(c => truncarNombre(c.categoriaNombre, 20) === labelStr);
                              return item ? item.categoriaNombre : labelStr;
                            }}
                          />
                          <Legend />
                          <Bar dataKey="totalVenta" fill="#22C55E" name="Total Venta">
                            {detalleCategoriaData.map((_, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                            <LabelList dataKey="totalVenta" position="right" formatter={(v) => {
                              const val = typeof v === 'number' ? v : 0;
                              return `$${Math.floor(val).toLocaleString('es-CL')}`;
                            }} />
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>

                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-green-50">
                            <tr>
                              <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Categoría</th>
                              <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Tipo</th>
                              <th className="px-6 py-4 text-right text-sm font-semibold text-gray-700">Cant. vendida</th>
                              <th className="px-6 py-4 text-right text-sm font-semibold text-gray-700">Total venta</th>
                              <th className="px-6 py-4 text-right text-sm font-semibold text-gray-700">Utilidad total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {detalleCategoriaData.map((cat, idx) => (
                              <tr key={idx} className={`border-t hover:bg-gray-50 ${cat.tipoCategoria === 'NINGUNA' ? 'text-gray-400 italic' : ''}`}>
                                <td className="px-6 py-4 text-sm font-medium">{cat.categoriaNombre}</td>
                                <td className="px-6 py-4 text-sm">
                                  {cat.tipoCategoria === 'PROPIA' && (
                                    <span className="px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-700">Propia</span>
                                  )}
                                  {cat.tipoCategoria === 'GLOBAL' && (
                                    <span className="px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-600">Global</span>
                                  )}
                                </td>
                                <td className="px-6 py-4 text-sm text-right">{formatearCantidad(cat.cantidadVendida)}</td>
                                <td className="px-6 py-4 text-sm text-right font-semibold">
                                  ${Math.floor(cat.totalVenta).toLocaleString('es-CL')}
                                </td>
                                <td className="px-6 py-4 text-sm text-right font-bold text-green-700">
                                  ${Math.floor(cat.utilidadTotal).toLocaleString('es-CL')}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12">
                    <p className="text-center text-gray-500">No hay datos disponibles</p>
                  </div>
                )}
              </div>
            )}

            {/* Contenido de Alertas de Inventario */}
            {tabActiva === 'alertas' && (
              <div>
                {loadingAlertas ? (
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12">
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-12 w-12 border-4 border-gray-200 border-t-red-500"></div>
                    </div>
                  </div>
                ) : alertasData && alertasData.length > 0 ? (
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-red-50">
                          <tr>
                            {(
                              [
                                { label: 'Código',            campo: 'codigo',           align: 'left'   },
                                { label: 'Producto',          campo: 'nombreProducto',   align: 'left'   },
                                { label: 'Fecha Vencimiento', campo: 'fechaVencimiento', align: 'left'   },
                                { label: 'Stock',             campo: 'stock',            align: 'center' },
                                { label: 'Estado',            campo: 'estado',           align: 'center' },
                              ] as const
                            ).map(({ label, campo, align }) => {
                              const activo = alertasSort?.campo === campo;
                              return (
                                <th
                                  key={campo}
                                  onClick={() => toggleSort(campo)}
                                  className={`px-6 py-4 text-${align} text-sm font-semibold text-gray-700 cursor-pointer select-none hover:bg-red-100 transition-colors`}
                                >
                                  <span className="inline-flex items-center gap-1">
                                    {label}
                                    <span className={`text-xs ${activo ? 'text-red-600' : 'text-gray-400'}`}>
                                      {activo ? (alertasSort?.dir === 'asc' ? '▲' : '▼') : '⇅'}
                                    </span>
                                  </span>
                                </th>
                              );
                            })}
                            <th className="px-6 py-4 text-center text-sm font-semibold text-gray-700">Acciones</th>
                          </tr>
                        </thead>
                        <tbody>
                          {alertasOrdenadas.map((alerta, idx) => (
                            <tr key={idx} className="border-t hover:bg-gray-50">
                              <td className="px-6 py-4 text-sm font-mono text-gray-600">
                                {alerta.codigo ?? <span className="text-gray-400 italic">Sin código</span>}
                              </td>
                              <td className="px-6 py-4 text-sm font-medium">{formatearNombreProducto(alerta.nombreProducto)}</td>
                              <td className="px-6 py-4 text-sm">{formatearFechaEspanol(alerta.fechaVencimiento)}</td>
                              <td className="px-6 py-4 text-sm text-center">{alerta.stock}</td>
                              <td className="px-6 py-4 text-center">
                                <span
                                  className={`px-3 py-1 rounded-full text-xs font-semibold ${
                                    alerta.estado === 'VENCIDO'
                                      ? 'bg-red-100 text-red-800'
                                      : 'bg-yellow-100 text-yellow-800'
                                  }`}
                                >
                                  {alerta.estado === 'VENCIDO' ? 'Vencido' : 'Por Vencer'}
                                </span>
                              </td>
                              <td className="px-6 py-4 text-center">
                                <div className="flex items-center justify-center gap-2">
                                  <button
                                    onClick={() =>
                                      alerta.productoAlmacenId
                                        ? setAlertaStockProductoId(alerta.productoAlmacenId)
                                        : undefined
                                    }
                                    disabled={!alerta.productoAlmacenId}
                                    className="px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors text-white hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed"
                                    style={{ backgroundColor: '#43BFFF' }}
                                    title="Agregar stock"
                                  >
                                    + Stock
                                  </button>
                                  <button
                                    onClick={() =>
                                      alerta.productoAlmacenId &&
                                      navigate(`/inventario/editar/${alerta.productoAlmacenId}`)
                                    }
                                    disabled={!alerta.productoAlmacenId}
                                    className="px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors text-white hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed"
                                    style={{ backgroundColor: '#11194F' }}
                                    title="Editar producto"
                                  >
                                    Editar
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : (
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12">
                    <p className="text-center text-gray-500">No hay alertas de inventario</p>
                  </div>
                )}
              </div>
            )}

            {/* Contenido de Detalle por Producto */}
            {tabActiva === 'detalle-producto' && (
              <div>
                {!busquedaRealizada ? (
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
                    <MdSearch className="text-6xl text-gray-300 mx-auto mb-4" />
                    <p className="text-xl text-gray-600">Selecciona un rango de fechas para ver el detalle por producto</p>
                  </div>
                ) : loadingDetalleProducto ? (
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12">
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-12 w-12 border-4 border-gray-200 border-t-purple-500"></div>
                    </div>
                  </div>
                ) : detalleProductoData && detalleProductoData.items.length > 0 ? (
                  <>
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-purple-50">
                            <tr>
                              <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Producto</th>
                              <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Categoría</th>
                              <th className="px-6 py-4 text-right text-sm font-semibold text-gray-700">Cantidad</th>
                              <th className="px-6 py-4 text-right text-sm font-semibold text-gray-700">P. Compra</th>
                              <th className="px-6 py-4 text-right text-sm font-semibold text-gray-700">P. Venta</th>
                              <th className="px-6 py-4 text-right text-sm font-semibold text-gray-700">Utilidad Unit.</th>
                              <th className="px-6 py-4 text-right text-sm font-semibold text-gray-700">Utilidad Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {detalleProductoData.items.map((producto, idx) => (
                              <tr key={idx} className="border-t hover:bg-gray-50">
                                <td className="px-6 py-4 text-sm font-medium">
                                  {formatearNombreProducto(producto.nombreProducto)}
                                </td>
                                <td className="px-6 py-4 text-sm">
                                  <span className="px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-700">
                                    {formatearNombreProducto(producto.categoria)}
                                  </span>
                                </td>
                                <td className="px-6 py-4 text-sm text-right">
                                  {formatearCantidad(producto.cantidadVendida)}
                                </td>
                                <td className="px-6 py-4 text-sm text-right">
                                  ${Math.floor(producto.precioCompra).toLocaleString('es-CL')}
                                </td>
                                <td className="px-6 py-4 text-sm text-right">
                                  ${Math.floor(producto.precioVenta).toLocaleString('es-CL')}
                                </td>
                                <td className="px-6 py-4 text-sm text-right text-green-600 font-semibold">
                                  ${Math.floor(producto.utilidad).toLocaleString('es-CL')}
                                </td>
                                <td className="px-6 py-4 text-sm text-right text-green-700 font-bold">
                                  ${Math.floor(producto.utilidadTotal).toLocaleString('es-CL')}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Paginación */}
                    {detalleProductoData.totalPaginas > 1 && (
                      <div className="mt-6 flex justify-center gap-2">
                        <button
                          onClick={() => setPaginaDetalleProducto(Math.max(0, paginaDetalleProducto - 1))}
                          disabled={paginaDetalleProducto === 0}
                          className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Anterior
                        </button>
                        <span className="px-4 py-2 bg-purple-500 text-white rounded-lg font-semibold">
                          Página {paginaDetalleProducto + 1} de {detalleProductoData.totalPaginas}
                        </span>
                        <button
                          onClick={() =>
                            setPaginaDetalleProducto(Math.min(detalleProductoData.totalPaginas - 1, paginaDetalleProducto + 1))
                          }
                          disabled={paginaDetalleProducto >= detalleProductoData.totalPaginas - 1}
                          className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Siguiente
                        </button>
                      </div>
                    )}
                    
                    {/* Información adicional */}
                    <div className="mt-4 text-sm text-gray-600 text-center">
                      Total de productos: {detalleProductoData.totalElementos}
                    </div>
                  </>
                ) : (
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12">
                    <p className="text-center text-gray-500">No hay datos disponibles</p>
                  </div>
                )}
              </div>
            )}
          </div>
      )}

      {/* Mensaje inicial cuando no hay búsqueda (y no viene de navegación) */}
      {!busquedaRealizada && !vieneDeNavegacion && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
          <MdSearch className="text-6xl text-gray-300 mx-auto mb-4" />
          <p className="text-xl text-gray-600">
            Selecciona un rango de fechas para ver los reportes
          </p>
        </div>
      )}

      {/* Modal de añadir stock desde alertas */}
      {alertaStockProductoId && (
        <AlertaStockModal
          productoAlmacenId={alertaStockProductoId}
          onClose={() => setAlertaStockProductoId(null)}
        />
      )}
    </div>
  );
};
